/**
 * The ONE place that decides which bearer token a browser caller sends to Foundations.
 *
 * WHY THIS EXISTS: /api/business-intelligence was mounted behind acceptCoordinatorOrIntegration
 * on 2026-07-19 (v3.65) to close an unauthenticated financial-data leak. Four browser call
 * sites had each grown their own idea of how to authenticate — three sent no credentials at
 * all and began 401-ing, and one sent `Bearer ` with an empty token. Centralising the decision
 * means the next gated route gets it right by default.
 *
 * The 2026-09 route-auth sweep gates every session-scoped route (coordinator, persona magic-link
 * or demo-viewer JWT + session ownership). Three roles store their token in three places, and
 * the old helper here only knew one of them:
 *   - persona (PersonaAccess.jsx)   → localStorage `foundations_persona_token`
 *   - demo viewer (DemoLogin.jsx)   → sessionStorage `authToken` / `token` / `foundations-token`
 *   - coordinator / PM (Login.jsx, PlatformBridge.jsx) → localStorage `authToken` / `token` /
 *     `foundations-token`
 *   - `defenseAuthToken` — stashed from a `?token=` URL param by businessIntelligenceService.
 *     Logout never cleared it, so it is a FALLBACK only: a stale one must never outrank the
 *     current login (session ownership would 404 the real user).
 *
 * Resolution order (services/api.js ApiService.getAuthToken delegates here):
 *   1. Persona context (`/persona/access`, or `/persona-assessment/...?locked=1`) → the persona
 *      token and NOTHING else — a coordinator token in the same browser must not leak into a
 *      persona's screen.
 *   2. sessionStorage demo-viewer token (cleared on browser close, so it is the most specific).
 *   3. localStorage login token.
 *   4. localStorage `defenseAuthToken`.
 *
 * Returning {} rather than an empty Bearer is deliberate: a malformed credential and an absent
 * one are different requests, and a gate may answer them differently.
 */
const PERSONA_TOKEN_KEY = 'foundations_persona_token';
const SESSION_TOKEN_KEYS = ['authToken', 'token', 'foundations-token'] as const;
const LOCAL_TOKEN_KEYS = ['authToken', 'token', 'foundations-token', 'defenseAuthToken'] as const;

function usable(token: string | null): string | null {
  return token && token.trim() ? token : null;
}

function safeGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function isPersonaContext(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const path = window.location.pathname || '';
  const search = window.location.search || '';
  return (
    path.startsWith('/persona/access') ||
    (path.startsWith('/persona-assessment/') && /[?&]locked=1\b/.test(search))
  );
}

export function getAuthToken(): string | null {
  const local = typeof localStorage !== 'undefined' ? localStorage : undefined;
  const session = typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;

  if (isPersonaContext()) {
    return usable(safeGet(local, PERSONA_TOKEN_KEY));
  }
  for (const key of SESSION_TOKEN_KEYS) {
    const token = usable(safeGet(session, key));
    if (token) return token;
  }
  for (const key of LOCAL_TOKEN_KEYS) {
    const token = usable(safeGet(local, key));
    if (token) return token;
  }
  return null;
}

export function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
